import type { TaskItem } from '../components/task-list/TaskList'
import type { TaskGroupItem } from '../components/task-group-list/TaskGroupList'
import type {
  Filters,
  PlanStatusEnum,
  Tasks,
  Task,
  PlanSummary
} from 'prove-common'
import {
  calculateSamplesTaken,
  calculateSamplesToTake,
  formatMatrixAmount,
  getNearestDuedate,
  getSubstanceText
} from 'prove-common'

export interface PlanChainNode {
  id: number
  name: string
  status: PlanSummary['status']
  parents: number[]
  children: PlanChainNode[]
}

export const buildPlanChain = (
  planId: number,
  plans: PlanSummary[] | undefined
): PlanChainNode | undefined => {
  if (!Array.isArray(plans) || plans.length === 0) return undefined
  const planMap = new Map(plans.map((p) => [p.id, p]))
  const path: PlanSummary[] = []
  const visited = new Set<number>()

  let current = planMap.get(planId)
  while (current && !visited.has(current.id)) {
    path.push(current)
    visited.add(current.id)
    const parentId = current.parents?.[0]
    current = parentId == null ? undefined : planMap.get(parentId)
  }

  if (path.length === 0) return undefined

  path.reverse() // Convert leaf->...->root to root->...->leaf

  let node: PlanChainNode | undefined
  for (let i = path.length - 1; i >= 0; i--) {
    const p = path[i]
    node = {
      id: p.id,
      name: p.name,
      status: p.status,
      parents: p.parents,
      children: node ? [node] : []
    }
  }
  return node
}

export const isTaskCompleted = (
  samplesTaken?: number,
  samplesToTake?: number
): boolean | undefined =>
  samplesTaken != null && samplesToTake != null
    ? samplesTaken >= samplesToTake
    : undefined

export const getTaskList = (
  tasks: Tasks,
  plans: PlanSummary[] | undefined
): TaskItem[] => {
  return tasks.map((task) => {
    const isCompleted = isTaskCompleted(task.totalCount, task.quantity) === true
    const details = getDetailsFromTask(task, plans)
    return {
      task,
      isCompleted,
      details
    }
  })
}

export const expandPlanIdsWithAncestors = (
  planIds: number[] | undefined,
  plans: PlanSummary[] | undefined
): number[] => {
  const byId = new Map((plans ?? []).map((plan) => [plan.id, plan]))
  const expanded = new Set<number>()

  for (const planId of planIds ?? []) {
    let current = byId.get(planId)
    const visited = new Set<number>()

    while (current != null && !visited.has(current.id)) {
      expanded.add(current.id)
      visited.add(current.id)
      const parentId = current.parents?.[0]
      current = parentId == null ? undefined : byId.get(parentId)
    }
  }

  return Array.from(expanded)
}

/**
 * True when `plan` is below (or is) any of `ancestorIds` in the plan
 * hierarchy. Used to collect the under-plan filter options below a selected
 * top-level plan, at any depth.
 */
export const hasSelectedPlanInChain = (
  plan: PlanSummary,
  ancestorIds: Set<number>,
  byId: Map<number, PlanSummary>
): boolean => {
  let current: PlanSummary | undefined = plan
  const visited = new Set<number>()

  while (current != null && !visited.has(current.id)) {
    if (ancestorIds.has(current.id)) return true
    visited.add(current.id)
    const parentId: number | undefined = current.parents?.[0]
    current = parentId == null ? undefined : byId.get(parentId)
  }

  return false
}

export const filterTaskList = (
  tasks: TaskItem[],
  filters: Filters<PlanStatusEnum>
): TaskItem[] => {
  const collectChainIds = (node: PlanChainNode | undefined): number[] => {
    if (!node) return []
    const ids = [node.id]
    for (const child of node.children) ids.push(...collectChainIds(child))
    return ids
  }

  const selectedSubPlans = new Set(filters.subPlanIds ?? [])

  return tasks.filter((item) => {
    const chainIds = collectChainIds(item.details.planChain).map(String)
    const chainIdsSet = new Set(chainIds)

    let passesPlanFilter: boolean
    if (selectedSubPlans.size > 0) {
      passesPlanFilter = chainIds.some((id) => selectedSubPlans.has(id))
    } else {
      passesPlanFilter =
        (filters.planIds?.length ?? 0) === 0 ||
        (filters.planIds ?? []).some((pid) => chainIdsSet.has(pid))
    }

    if (!passesPlanFilter) return false
    const taskProductId =
      item.task.analysisType?.sampleType?.productId.toString()
    const taskMatrixId = item.task.analysisType?.sampleType?.matrixId.toString()
    const taskSampntTypeId = item.task.sampntType?.id.toString()
    const taskPurposeId = item.task.purposeId?.toString()
    const taskSubPurposeId = item.task.subPurposeId?.toString()
    const taskSubstanceIds =
      item.task.analysisType?.substances?.map((s) =>
        s.substance.id.toString()
      ) ?? []
    return (
      (filters.productIds?.length === 0 ||
        filters.productIds?.includes(taskProductId ?? '')) &&
      (filters.matrixIds?.length === 0 ||
        filters.matrixIds?.includes(taskMatrixId ?? '')) &&
      (filters.sampntTypeIds?.length === 0 ||
        filters.sampntTypeIds?.includes(taskSampntTypeId ?? '')) &&
      (filters.purposeIds == null ||
        filters.purposeIds?.length === 0 ||
        filters.purposeIds?.includes(taskPurposeId ?? '')) &&
      (filters.subPurposeIds == null ||
        filters.subPurposeIds?.length === 0 ||
        filters.subPurposeIds?.includes(taskSubPurposeId ?? '')) &&
      filters.substanceIds?.every((value) =>
        taskSubstanceIds.includes(value)
      ) &&
      (filters.labId == null || filters.labId === item.task.lab?.id.toString())
    )
  })
}

export type SortTasksBy = 'dueDate' | 'samplesLeft' | 'priority' | 'createdDate'

export function getClosestDueDate(
  duedates: string[] | null | undefined
): string | null {
  return getNearestDuedate(duedates) ?? null
}

type DueDateCategory = 'future' | 'past' | 'none'

interface WithDueDates {
  duedates?: string[] | null
}

interface CategorizedItem<T> {
  item: T
  category: DueDateCategory
  closestDate: string | null
}

/**
 * Categorizes and sorts items by due date.
 * Order: future dates (ascending) → past dates (ascending) → no dates (by fallback)
 */
const sortByDueDate = <T extends WithDueDates>(
  items: T[],
  getFallbackOrder: (a: T, b: T) => number
): T[] => {
  const today = new Date().toISOString().slice(0, 10)

  const categorized: CategorizedItem<T>[] = items.map((item) => {
    const duedates = item.duedates
    if (!duedates || duedates.length === 0) {
      return { item, category: 'none' as const, closestDate: null }
    }
    const hasFuture = duedates.some((d) => d.slice(0, 10) >= today)
    return {
      item,
      category: hasFuture ? ('future' as const) : ('past' as const),
      closestDate: getClosestDueDate(duedates)
    }
  })

  const compareByDate = (a: CategorizedItem<T>, b: CategorizedItem<T>) => {
    if (!a.closestDate && !b.closestDate)
      return getFallbackOrder(a.item, b.item)
    if (!a.closestDate) return 1
    if (!b.closestDate) return -1
    return new Date(a.closestDate).getTime() - new Date(b.closestDate).getTime()
  }

  const categoryOrder: Record<DueDateCategory, number> = {
    future: 0,
    past: 1,
    none: 2
  }

  categorized.sort((a, b) => {
    const categoryDiff = categoryOrder[a.category] - categoryOrder[b.category]
    if (categoryDiff !== 0) return categoryDiff
    if (a.category === 'none') return getFallbackOrder(a.item, b.item)
    return compareByDate(a, b)
  })

  return categorized.map((c) => c.item)
}

export const sortTaskList = (tasks: TaskItem[], sortBy: SortTasksBy) => {
  if (sortBy === 'dueDate') {
    return sortByDueDate(
      tasks.map((t) => ({ ...t, duedates: t.task.duedates })),
      (a, b) => b.task.id - a.task.id
    ).map(({ ...rest }) => rest as TaskItem)
  }
  return [...tasks].sort((a, b) => {
    if (sortBy === 'samplesLeft') {
      // Sorting by remaining samples (quantity - directCount)
      const aSamplesLeft = (a.task?.quantity ?? 0) - (a.task?.directCount ?? 0)
      const bSamplesLeft = (b.task?.quantity ?? 0) - (b.task?.directCount ?? 0)
      return bSamplesLeft - aSamplesLeft
    } else if (sortBy === 'createdDate') {
      return b.task.id - a.task.id
    } else {
      return 0
    }
  })
}

export const sortTaskByDuedate = (tasks: Tasks): Tasks => {
  return sortByDueDate(tasks, (a, b) => b.id - a.id)
}

// Updated to attach hierarchy inside details; signature unchanged for now
export const groupTaskList = (
  tasks: TaskItem[],
  plans: PlanSummary[] | undefined
): TaskGroupItem[] => {
  const items: { [key: string]: TaskItem[] } = {}
  for (const task of tasks) {
    const key = JSON.stringify({
      planIds: task.details.planIds,
      planStatus: task.details.planStatus,
      sampntTypeName: task.details.sampntTypeName,
      product: task.details.product,
      matrix: task.details.matrix,
      labName: task.details.labName,
      countSubSamples: task.details.countSubSamples,
      ownerId: task.details.ownerId,
      analysisTypeApprovalStatus: task.details.analysisTypeApprovalStatus
    })
    if (!items[key]) items[key] = []
    items[key].push(task)
  }
  return Object.entries(items).map(([key, taskItems]) => {
    const groupId = taskItems.map((item) => item.task.id).join(',')
    const details = getDetailsFromTasks(
      taskItems.map((item) => item.task),
      plans
    )
    const isCompleted = taskItems.every((item) => item.isCompleted)
    return {
      key,
      groupId,
      details,
      isCompleted
    }
  })
}

const isTaskDetailsCompatible = (detailsArray: TaskDetails[]) => {
  const firstDetails = detailsArray[0]
  return detailsArray.every((details) => {
    return (
      details.planIds?.toString() === firstDetails.planIds?.toString() &&
      details.planStatus === firstDetails.planStatus &&
      details.sampntTypeName === firstDetails.sampntTypeName &&
      details.product === firstDetails.product &&
      details.matrix === firstDetails.matrix &&
      details.labName === firstDetails.labName &&
      details.countSubSamples === firstDetails.countSubSamples &&
      details.ownerId === firstDetails.ownerId &&
      details.analysisTypeApprovalStatus ===
        firstDetails.analysisTypeApprovalStatus
    )
  })
}

export const getDetailsFromTask = (
  task: Task,
  plans: PlanSummary[] | undefined
): TaskDetails => {
  const planIds = task.planIds
  const planStatus = task.planStatus
  const sampntTypeName = task?.sampntType?.nameNo
  const product = task?.analysisType?.sampleType?.product
  const matrix = task?.analysisType?.sampleType?.matrix
  const analysis = getSubstanceText(task.analysisType) ?? '-'
  const labName = task?.lab?.name
  const countSubSamples = task?.countSubSamples
  const locationRequired = task?.locationRequired ?? false
  const samplesTaken = task?.totalCount
  const samplesToTake = task?.quantity
  const firstPlanId = planIds?.[0]
  const planChain = firstPlanId ? buildPlanChain(firstPlanId, plans) : undefined
  const duedate = task.duedates
  const analysisTypeApprovalStatus = task.analysisTypeApprovalStatus
  const approvalComment = task.approvalComment
  const matrixAmount = formatMatrixAmount(task.matrixAmount)

  return {
    planIds,
    planStatus,
    sampntTypeName,
    product,
    matrix,
    matrixAmount,
    analysis,
    labName,
    countSubSamples,
    samplesTaken,
    samplesToTake,
    ownerId: task?.ownerId,
    locationRequired,
    planChain,
    duedate: getClosestDueDate(duedate),
    analysisTypeApprovalStatus,
    approvalComment
  }
}

export interface TaskDetails {
  planIds: number[] | undefined
  planStatus: PlanStatusEnum | undefined
  sampntTypeName: string | undefined
  product: string | undefined
  matrix: string | undefined
  /** Formatert mengde prøvemateriale, f.eks. "300 g" */
  matrixAmount?: string
  analysis: string | undefined
  countSubSamples: boolean | undefined
  labName: string | undefined
  locationRequired?: boolean

  samplesTaken: number | undefined
  samplesToTake: number | undefined

  ownerId?: string

  planChain?: PlanChainNode
  duedate?: string | null
  analysisTypeApprovalStatus?: 'approved' | 'declined' | 'awaiting' | null
  approvalComment?: string | null
}

/**
 * Slår sammen mengde prøvemateriale for grupperte oppgaver. Ulike mengder
 * vises alle (kommaseparert) slik at ingen informasjon skjules; like mengder
 * vises én gang. Returnerer undefined når ingen oppgave har mengde.
 */
export const mergeMatrixAmounts = (
  details: Pick<TaskDetails, 'matrixAmount'>[]
): string | undefined => {
  const amounts = Array.from(
    new Set(
      details.map((d) => d.matrixAmount).filter((a): a is string => a != null)
    )
  )
  return amounts.length > 0 ? amounts.join(', ') : undefined
}

export const getDetailsFromTasks = (
  tasks: Tasks,
  plans?: PlanSummary[]
): TaskDetails => {
  const taskDetails = tasks.map((task) => getDetailsFromTask(task, plans))
  if (!taskDetails.length) {
    throw new Error('Mangler oppgaver')
  }
  if (!isTaskDetailsCompatible(taskDetails)) {
    throw new Error('Oppgavene er ikke kompatible med hverandre')
  }
  const {
    planChain,
    planIds,
    planStatus,
    product,
    matrix,
    labName,
    sampntTypeName,
    countSubSamples,
    locationRequired,
    ownerId
  } = taskDetails[0]
  const analysis = taskDetails.map((task) => task.analysis).join('\n')
  const matrixAmount = mergeMatrixAmounts(taskDetails)
  const samplesTaken = calculateSamplesTaken(tasks)
  const samplesToTake = calculateSamplesToTake(tasks)
  const ongoingTasks = tasks.filter(
    (task) => isTaskCompleted(task.totalCount, task.quantity) !== true
  )
  const tasksWithRelevantDuedates =
    ongoingTasks.length > 0 ? ongoingTasks : tasks
  const allDuedates = tasksWithRelevantDuedates
    .flatMap((task) => task.duedates ?? [])
    .filter((d): d is string => d != null)
  const duedate = getClosestDueDate(allDuedates)
  const analysisTypeApprovalStatus = taskDetails[0].analysisTypeApprovalStatus
  const approvalComment = taskDetails[0].approvalComment

  return {
    planIds,
    analysis,
    countSubSamples,
    planStatus,
    product,
    matrix,
    matrixAmount,
    labName,
    sampntTypeName,
    samplesTaken,
    samplesToTake,
    locationRequired,
    ownerId,
    planChain,
    duedate,
    analysisTypeApprovalStatus,
    approvalComment
  }
}

export const collectPlanChainNames = (
  root?: PlanChainNode
): string[] | undefined => {
  if (!root) return undefined
  const names: string[] = []
  const traverse = (node: PlanChainNode) => {
    names.push(node.name)
    for (const child of node.children) traverse(child)
  }
  traverse(root)
  return names
}

export const formatPlanChain = (
  root?: PlanChainNode,
  separator = ' → '
): string | undefined => {
  const names = collectPlanChainNames(root)
  return names?.join(separator)
}
